/**
 * Subscription-banner dismissal storage — METALARK-1189
 *
 * A dismissal is scoped two ways, and both matter:
 *
 *  - By notice. The entry records WHICH notice was dismissed. Before this,
 *    dismiss() stored only a timestamp plus the severity of the banner being
 *    dismissed, and the banner applied that entry to whatever notice was due
 *    on the next mount — so dismissing an upcoming-renewal reminder suppressed
 *    a later Payment Failed, Subscription Ended or Trial Ended banner for the
 *    rest of the 24 hours. A stored severity cannot gate this: every urgent
 *    branch sets canDismiss = false, so the severity written was always 'info'.
 *
 *  - By customer. A dismissal made in one workspace must not follow the user
 *    into another customer whose subscription is in a different state. Same
 *    composite-key approach as custom-banner dismissals
 *    (`domainbanner_dismissed_${customerId}` in custom-domain-banner.tag).
 *
 * A notice key deliberately excludes the day count it renders. `renewal_soon`
 * covers "Renewal in 3 Days" and "Renewal in 2 Days" alike, so a midnight
 * rollover cannot resurrect a reminder inside its own 24-hour window.
 *
 * Entries written before this change have no noticeKey and therefore never
 * match, which fails safe: at worst one already-dismissed reminder reappears.
 */

export const DISMISSAL_TTL_HOURS = 24;

const KEY_PREFIX = 'subscription-banner-dismissed';

/**
 * Every notice the banner can render. NOTICE_KEYS is the authoritative list;
 * subscription-banner.tag assigns from it and tests/unit asserts the two agree.
 */
export const NOTICE_KEYS = Object.freeze({
    TRIAL_ENDING: 'trial_ending',
    TRIAL_ENDED: 'trial_ended',
    GRACE_PERIOD: 'grace_period',
    GRACE_FINAL_DAY: 'grace_final_day',
    GRACE_EXPIRED: 'grace_expired',
    SUBSCRIPTION_CANCELED: 'subscription_canceled',
    SUBSCRIPTION_ENDING_SOON: 'subscription_ending_soon',
    SUBSCRIPTION_ENDING_TODAY: 'subscription_ending_today',
    SUBSCRIPTION_ENDED: 'subscription_ended',
    SUBSCRIPTION_EXPIRED: 'subscription_expired',
    PAYMENT_FAILED: 'payment_failed',
    RENEWAL_FAILED: 'renewal_failed',
    RENEWAL_SOON: 'renewal_soon',
});

/**
 * Notices a user is allowed to dismiss. The banner still decides per-state
 * whether the button appears (a trial with 2 days left is not dismissible even
 * though `trial_ending` is in this list); this is the outer bound.
 */
export const DISMISSIBLE_NOTICE_KEYS = Object.freeze([
    NOTICE_KEYS.TRIAL_ENDING,
    NOTICE_KEYS.SUBSCRIPTION_ENDING_SOON,
    NOTICE_KEYS.RENEWAL_SOON,
]);

export function dismissalStorageKey(customerId) {
    return customerId ? `${KEY_PREFIX}_${customerId}` : KEY_PREFIX;
}

export function readDismissal(customerId, storage = globalThis.localStorage) {
    let parsed = null;
    try {
        const raw = storage?.getItem(dismissalStorageKey(customerId));
        parsed = raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }

    if (!parsed || typeof parsed.noticeKey !== 'string' || typeof parsed.timestamp !== 'number') {
        return null;
    }
    return parsed;
}

export function isDismissalActive(noticeKey, customerId, storage = globalThis.localStorage, now = Date.now()) {
    if (!noticeKey) {
        return false;
    }

    const stored = readDismissal(customerId, storage);
    if (!stored || stored.noticeKey !== noticeKey) {
        return false;
    }

    return (now - stored.timestamp) / (1000 * 60 * 60) < DISMISSAL_TTL_HOURS;
}

export function recordDismissal(noticeKey, customerId, storage = globalThis.localStorage, now = Date.now()) {
    const entry = { noticeKey, timestamp: now };
    try {
        storage?.setItem(dismissalStorageKey(customerId), JSON.stringify(entry));
    } catch {
        // A full or blocked localStorage costs the user a re-dismissal, nothing more.
    }
    return entry;
}
